import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Award,
  BarChart3,
  CloudUpload,
  Crosshair,
  Database,
  Download,
  Eye,
  Globe,
  Landmark,
  MoreHorizontal,
  PieChart,
  PlusCircle,
  Target,
  Trash2,
  Trophy,
  UserCog,
  Users,
} from "lucide-react";
import { AdminTopbar } from "../../layouts/AdminLayout";
import { Badge, EmptyState, EventTypeChip, SectionCard, StatCard, statusTone } from "../../components/ui";
import { useApi } from "../../api/client";
import { Competition, ImportRecord } from "../../data/types";
import { usePortalLink } from "../../utils/portalLink";

/** Icon treatment per audit-log action. */
const activityIcons: Record<string, { icon: React.ComponentType<{ size?: number }>; cls: string }> = {
  created: { icon: PlusCircle, cls: "bg-success-soft text-success" },
  updated: { icon: UserCog, cls: "bg-warning-soft text-warning" },
  imported: { icon: CloudUpload, cls: "bg-violet-50 text-violet-600" },
  recalculated: { icon: Trophy, cls: "bg-primary-soft text-primary" },
  deleted: { icon: Trash2, cls: "bg-danger-soft text-danger" },
};

/*
 * The Association's three levels, in its own words:
 *   event       — the meeting (EID), "Blue Mountains OPM"
 *   competition — a trophy shot within it (CID), "Bronger", "Carrington"
 *   match       — one range (MID), "Bronger 600-1"
 * `days` is how many dates the meeting spans, which is not a level of its own.
 */
/*
 * Bytes as a person reads them. The dashboard answers "how much room is this
 * taking" — a nine-digit number answers it in principle and not in practice.
 */
const formatBytes = (n?: number | null): string => {
  if (!n || n <= 0) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  const v = n / 1024 ** i;
  return `${v >= 100 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
};

interface DashboardStats {
  events: number;
  competitions: number;
  days: number;
  matches: number;
  shooters: number;
  results: number;
  imports: number;
  /* Who is using it, and how much room it takes — the Association asked for
     both under a heading about capacity. */
  clubs: number;
  countries: number;
  users: number;
  strings: number;
  shots: number;
  views: number;
  storage: { bytes: number; documents: number } | null;
}

interface AuditEntry {
  action: string;
  kind: string;
  detail: string;
  user: string;
  atLabel: string;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const link = usePortalLink();
  const { data: stats } = useApi<DashboardStats | null>("/stats", null);
  const { data: competitions } = useApi<Competition[]>("/competitions", []);
  const { data: activity } = useApi<AuditEntry[]>("/admin/audit-log", []);
  const { data: imports } = useApi<ImportRecord[]>("/imports", []);
  const { data: storedFaces } = useApi<{ name: string }[]>("/target-faces", []);
  const { data: missingFaces } = useApi<string[]>("/target-faces/missing", []);

  const live = competitions.filter((c) => c.status === "LIVE").length;
  const shotsImported = imports.reduce((a, r) => a + (r.records || 0), 0);
  const lastImport = imports[0];

  return (
    <div>
      <AdminTopbar title="Dashboard" subtitle="Everything below is generated from imported shoot data." />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
        <StatCard icon={<Trophy size={20} />} value={String(stats?.events ?? 0)} label="Events" sub={`${live} Live`} subTone="primary" />
        <StatCard icon={<Award size={20} />} value={String(stats?.competitions ?? 0)} label="Competitions" iconBg="bg-success-soft text-success" />
        <StatCard icon={<Crosshair size={20} />} value={String(stats?.matches ?? 0)} label="Matches" sub="Ranges shot" iconBg="bg-violet-50 text-violet-600" />
        <StatCard icon={<Users size={20} />} value={(stats?.shooters ?? 0).toLocaleString()} label="Competitors" iconBg="bg-warning-soft text-warning" />
        <StatCard icon={<BarChart3 size={20} />} value={(stats?.results ?? 0).toLocaleString()} label="Result Rows" />
        <StatCard icon={<PieChart size={20} />} value={String(stats?.imports ?? 0)} label="Imports" sub={lastImport ? `Last: ${lastImport.date}` : "None yet"} iconBg="bg-danger-soft text-danger" />
      </div>

      {/*
        Who uses the portal and how much room it takes. Kept as a second row so
        the shooting figures above stay the first thing read.
      */}
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard icon={<Landmark size={20} />} value={String(stats?.clubs ?? 0)} label="Clubs" sub="On the register" />
        <StatCard
          icon={<Globe size={20} />}
          value={String(stats?.countries ?? 0)}
          label="Countries"
          sub="Clubs, competitors and events"
          iconBg="bg-primary-soft text-primary"
        />
        <StatCard icon={<UserCog size={20} />} value={String(stats?.users ?? 0)} label="User Accounts" sub="Association and club logins" iconBg="bg-violet-50 text-violet-600" />
        <StatCard
          icon={<Eye size={20} />}
          value={(stats?.views ?? 0).toLocaleString()}
          label="Page Views"
          sub="Public pages, all time"
          iconBg="bg-success-soft text-success"
        />
        <StatCard
          icon={<Database size={20} />}
          value={formatBytes(stats?.storage?.bytes)}
          label="Data Stored"
          sub={`${(stats?.strings ?? 0).toLocaleString()} strings · ${(stats?.shots ?? 0).toLocaleString()} shots`}
          iconBg="bg-warning-soft text-warning"
        />
      </div>

      <div className="mt-3 grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0 space-y-6">
          <SectionCard
            title="Recent Events"
            action={<Link to="/admin/competitions" className="text-sm font-semibold text-primary hover:underline">View All</Link>}
          >
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px]">
                <thead>
                  <tr className="border-b border-line bg-slate-50">
                    <th className="th">Event</th>
                    <th className="th">Type</th>
                    <th className="th">Level</th>
                    <th className="th">Date Range</th>
                    <th className="th text-center">Days</th>
                    <th className="th text-center">Matches</th>
                    <th className="th">Status</th>
                    <th className="th text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {!competitions.length && (
                    <tr>
                      <td colSpan={8}>
                        <EmptyState
                          title="No events yet"
                          message="Upload a shoot workbook on Import Data and the event, its competitions, ranges, competitors and results are all created for you."
                          action={
                            <Link
                              to="/admin/import"
                              className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-4 text-[13px] font-bold text-white transition-colors hover:bg-primary-hover"
                            >
                              <CloudUpload size={14} /> Import a Shoot Workbook
                            </Link>
                          }
                        />
                      </td>
                    </tr>
                  )}
                  {competitions.slice(0, 5).map((c) => (
                    <tr key={c.id} onClick={() => navigate(link(`/competitions/${c.id}`))} className="cursor-pointer transition-colors hover:bg-slate-50">
                      <td className="td">
                        <span className="flex items-center gap-3">
                          <Target size={18} className="shrink-0 text-primary" />
                          <span>
                            <Link to={link(`/competitions/${c.id}`)} className="font-semibold hover:text-primary">
                              {c.name}
                            </Link>
                            {/* Where it was shot, as on every other list. */}
                            <span className="block text-[12px] text-muted">
                              {[c.range, c.stateCode, c.country].filter(Boolean).join(", ")}
                            </span>
                          </span>
                        </span>
                      </td>
                      <td className="td"><EventTypeChip type={c.eventStatus || c.type} /></td>
                      <td className="td text-slate-600">{c.eventLevel || "—"}</td>
                      <td className="td text-muted">
                        {c.startDate}
                        {c.endDate && c.endDate !== c.startDate ? ` – ${c.endDate}` : ""}
                      </td>
                      <td className="td text-center">{c.events}</td>
                      <td className="td text-center">{c.matches}</td>
                      <td className="td"><Badge tone={statusTone(c.status)}>{c.status}</Badge></td>
                      <td className="td text-right">
                        <button className="rounded-lg p-2 text-muted transition-colors hover:bg-slate-100" aria-label="More actions">
                          <MoreHorizontal size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SectionCard>

          <div className="card grid grid-cols-2 divide-x divide-line lg:grid-cols-4">
            {[
              { icon: Users, v: (stats?.shooters ?? 0).toLocaleString(), l: "Competitors", s: "On the SID register", cls: "text-success" },
              { icon: Target, v: (stats?.results ?? 0).toLocaleString(), l: "Result Rows", s: "Match + aggregate scopes", cls: "text-primary" },
              { icon: Download, v: shotsImported.toLocaleString(), l: "Shots Imported", s: "Across all imports", cls: "text-violet-600" },
              { icon: Database, v: String(stats?.imports ?? 0), l: "Import Runs", s: lastImport ? `Last ${lastImport.date}` : "None yet", cls: "text-warning" },
            ].map((x) => (
              <div key={x.l} className="flex items-center gap-3.5 px-5 py-5">
                <x.icon size={24} className={x.cls} />
                <div>
                  <div className="text-xl font-extrabold text-ink">{x.v}</div>
                  <div className="text-[13px] font-semibold text-ink">{x.l}</div>
                  <div className="text-xs text-muted">{x.s}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          {/*
            Real checks on the data, not four hard-coded "Operational" lights.
            Every line is something an administrator can act on, and links to the
            page where it is acted on.
          */}
          <SectionCard title="Portal Health" bodyClassName="px-5 py-2">
            {[
              {
                label: "Target definitions",
                ok: storedFaces.length > 0,
                detail: storedFaces.length ? `${storedFaces.length} faces stored` : "None loaded yet",
                to: "/admin/targets",
              },
              {
                label: "Faces used but undefined",
                ok: missingFaces.length === 0,
                detail: missingFaces.length ? `${missingFaces.length} to import` : "None",
                to: "/admin/targets",
              },
              {
                label: "Shoot data",
                ok: (stats?.results ?? 0) > 0,
                detail: (stats?.results ?? 0) > 0 ? `${(stats?.results ?? 0).toLocaleString()} result rows` : "Nothing imported",
                to: "/admin/import",
              },
              {
                label: "Last upload",
                ok: !!lastImport,
                detail: lastImport ? lastImport.date : "Never",
                to: "/admin/import-history",
              },
            ].map((row) => (
              <Link
                key={row.label}
                to={row.to}
                className="flex items-center justify-between border-b border-line py-2.5 text-[13px] last:border-0 hover:text-primary"
              >
                <span className="text-slate-600">{row.label}</span>
                <span className={`flex items-center gap-1.5 font-semibold ${row.ok ? "text-success" : "text-warning"}`}>
                  <span className={`h-2 w-2 rounded-full ${row.ok ? "bg-success" : "bg-warning"}`} /> {row.detail}
                </span>
              </Link>
            ))}
          </SectionCard>

          {/* Straight from the admin audit trail */}
          <SectionCard
            title="Recent Audits"
            action={<Link to="/admin/audit-log" className="text-sm font-semibold text-primary hover:underline">View All</Link>}
            /* Bounded so the feed scrolls in place instead of stretching the
               dashboard — the panels beside it stay in view. */
            bodyClassName="slim-scrollbar max-h-[320px] overflow-y-auto px-5 py-1"
          >
            {activity.slice(0, 8).map((a, i) => {
              const conf = activityIcons[a.action] ?? activityIcons.updated;
              return (
                <div key={i} className="flex items-start gap-3 border-b border-line py-2.5 last:border-0">
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${conf.cls}`}>
                    <conf.icon size={16} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold capitalize text-ink">
                      {a.action} {a.kind}
                    </div>
                    <div className="truncate text-[13px] text-muted">{a.detail}</div>
                  </div>
                  <span className="shrink-0 text-xs text-slate-400">{a.atLabel}</span>
                </div>
              );
            })}
            {!activity.length && <p className="py-6 text-center text-[13px] text-muted">No admin activity recorded yet.</p>}
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
